import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useTranslation } from 'react-i18next'

import { Button, DrawerStepper, showToast } from '@mercai/clever'

import { SupplyInventoryTransactionForm } from '@/components'
import {
  QUERIES_ADMIN,
  useInventoryTransactionDetail,
  useUpdateInventoryTransaction,
} from '@/services'
import { InventoryTransactionProps } from '@/types'
import { useQueryClient } from '@tanstack/react-query'

import { SupplyInventoryTransactionDrawerProps } from './supply-inventory-transaction-drawer.type'

import styles from './supply-inventory-transaction-drawer.module.css'

export const SupplyInventoryTransactionDrawer = ({
  isOpen,
  onClose,
  uuid,
}: SupplyInventoryTransactionDrawerProps) => {
  const { t } = useTranslation('screenInventoryDetail')

  const { uuid: inventoryTransactionUuid } = useParams()

  const queryclient = useQueryClient()

  const [values, setValues] = useState<Partial<InventoryTransactionProps>>({})

  const { data } = useInventoryTransactionDetail(
    uuid || inventoryTransactionUuid,
  )

  const { mutateAsync, isPending } = useUpdateInventoryTransaction()

  const onHandleClose = () => {
    onClose()
    setValues({})
  }

  const onHandleSubmit = async () => {
    try {
      const response = await mutateAsync({
        uuid: uuid || inventoryTransactionUuid,
        ...values,
      })

      if (!response.success) {
        showToast({
          variant: 'error',
          title: t('supplyInventoryTransactionDrawerErrorMessage'),
        })
        return
      }

      showToast({
        variant: 'success',
        title: t('supplyInventoryTransactionDrawerSuccessMessage'),
      })

      queryclient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.INVENTORY.module],
      })

      onHandleClose()
    } catch (error) {
      showToast({
        variant: 'error',
        title: t('supplyInventoryTransactionDrawerErrorMessage'),
      })
    }
  }

  useEffect(() => {
    if (data?.data) {
      setValues({
        batchCode: data?.data?.batchCode,
        entryDate: data?.data?.entryDate,
        internalCode: data?.data?.internalCode,
      })
    }
  }, [data?.data])

  return (
    <DrawerStepper
      isOpen={isOpen}
      onHandleOpen={onClose}
      initialStep="root"
      translations={{
        optional: '',
      }}
      title={t('supplyInventoryTransactionDrawerTitle')}
      steps={{
        root: {
          backNavigate: 'root',
          isOptional: true,
          component: (
            <SupplyInventoryTransactionForm
              values={values}
              onChangeValues={setValues}
              errors={{}}
            />
          ),
          footer: (
            <div className={styles.footer}>
              <Button variant="secondary" onClick={onHandleClose}>
                {t('cancelButton')}
              </Button>
              <Button
                onClick={onHandleSubmit}
                isLoading={isPending}
                disabled={isPending}
              >
                {t('confirmButton')}
              </Button>
            </div>
          ),
        },
      }}
    />
  )
}
